// Shared types and utilities for dashboard components
import moment from 'moment';
import { AppNameMap } from '../../../../../shared/utils/engagementAnalyticsTransformers';
import { TransferReason, TransferCategoryData, TRANSFER_REASON_LABELS, TRANSFER_REASON_IS_PLANNED } from '../../../../../shared/components/charts/TransfersChart';
import { TranscriptRecord } from './ChartDetailModal';

// Type for histogram view dropdown options
export type HistogramViewOption = "Containable Conversations" | "Order Calls" | "Spanish Calls" | "All Conversations" | "Average Calls by Hour" | "Average Calls by Day of Week" | "Concurrent Calls";

// Shared dashboard props interface
export interface BaseDashboardProps {
    organizationId: string;
    dateRange: string;
    startDate: string;
    endDate: string;
    selectedAppIds: Set<string>;
    isChartsLoading: boolean;
    totalAppCount: number;
    appNameMap: AppNameMap;
    isMultiLingual: boolean;
    isSpanishOnlyFilter?: boolean;
    isOutboundCallApp?: boolean;
    onLoadingChange?: (isLoading: boolean) => void;
    /** Incrementing counter to force a re-fetch of analytics with the current filters */
    refreshKey?: number;
}

// Call data record interface
export interface CallDataRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    transcriptLink: string;
    category: string;
    tags: string[];
    location: string;
    applicationId: string;
    sessionId: string;
    isContainable: boolean;
    isContained: boolean;
    orderTotal?: number;
    sessionSummaries?: string;
}

// Transfer data record interface
export interface TransferDataRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    transcriptLink: string;
    transferType: 'Planned' | 'Unplanned' | 'Undetermined';
    transferReason: TransferReason;
    location: string;
    applicationId: string;
    sessionId: string;
    sessionSummaries?: string;
}

// Sort configuration interface
export interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

// Utility function to calculate transfer totals
export const calculateTransferTotals = (transfersData: TransferCategoryData[]) => {
    // Early return for empty data
    if (!transfersData || transfersData.length === 0) {
        return { planned: 0, unplanned: 0, undetermined: 0, total: 0 };
    }

    // Pre-index by category for O(1) lookups instead of multiple .find() calls
    const byCategory = new Map<string, TransferCategoryData>();
    for (const d of transfersData) {
        byCategory.set(d.category, d);
    }

    const getTotal = (categoryData: TransferCategoryData | undefined): number => {
        if (!categoryData) return 0;
        let sum = 0;
        for (const key in categoryData) {
            if (key === 'category') continue;
            const reasonData = categoryData[key] as { value: number; apps: { appName: string; count: number }[] } | number;
            sum += typeof reasonData === 'number' ? reasonData : reasonData?.value ?? 0;
        }
        return sum;
    };

    const plannedTotal = getTotal(byCategory.get('Planned'));
    const unplannedTotal = getTotal(byCategory.get('Unplanned'));
    const undeterminedTotal = getTotal(byCategory.get('Undetermined'));

    return {
        planned: plannedTotal,
        unplanned: unplannedTotal,
        undetermined: undeterminedTotal,
        total: plannedTotal + unplannedTotal + undeterminedTotal
    };
};

// Utility function to get counts of unsupported transfers (unsupported requests/appointment types)
// These count toward containment as they represent properly handled calls
export const getUnsupportedTransferCounts = (transfersData: TransferCategoryData[] | null) => {
    if (!transfersData || transfersData.length === 0) {
        return { unsupportedRequests: 0, unsupportedAppointmentTypes: 0, total: 0 };
    }

    const plannedCategory = transfersData.find(d => d.category === 'Planned');
    if (!plannedCategory) {
        return { unsupportedRequests: 0, unsupportedAppointmentTypes: 0, total: 0 };
    }

    const getReasonCount = (reasonKey: TransferReason): number => {
        const reasonData = plannedCategory[reasonKey] as { value: number; apps: any[] } | number | undefined;
        if (!reasonData) return 0;
        return typeof reasonData === 'number' ? reasonData : reasonData?.value ?? 0;
    };

    const unsupportedRequests = getReasonCount(TransferReason.UNSUPPORTED_REQUEST);
    const unsupportedAppointmentTypes = getReasonCount(TransferReason.UNSUPPORTED_APPOINTMENT_TYPE);

    return {
        unsupportedRequests,
        unsupportedAppointmentTypes,
        total: unsupportedRequests + unsupportedAppointmentTypes
    };
};

// Helper to format date/time only once per engagement
// Reduces moment() calls from 2 per record to 1 by parsing once
const formatEventDateTime = (eventDate: string | Date): { date: string; time: string } => {
    const m = moment(eventDate);
    return {
        date: m.format('YYYY-MM-DD'),
        time: m.format('h:mm A')
    };
};

// Transform engagements to call data records
// Optimized: Single moment() call per record instead of two
export const transformEngagementsToCallRecords = (
    engagements: any[] | undefined,
    appNameMap: AppNameMap
): CallDataRecord[] => {
    if (!engagements || engagements.length === 0) return [];

    // Pre-allocate array size for better memory efficiency
    const result: CallDataRecord[] = new Array(engagements.length);

    for (let i = 0; i < engagements.length; i++) {
        const engagement = engagements[i];
        const { date, time } = formatEventDateTime(engagement.eventDate);

        result[i] = {
            id: engagement.id,
            date,
            time,
            phoneLastFour: engagement.nativeUserId?.slice(-4) || '****',
            transcriptLink: `/v/apps/${engagement.applicationId}/analytics?sessionId=${engagement.sessionId}`,
            category: engagement.sessionCategory || 'Unknown',
            tags: engagement.sessionTags || [],
            location: appNameMap[engagement.applicationId] || engagement.applicationId,
            applicationId: engagement.applicationId,
            sessionId: engagement.sessionId,
            isContainable: engagement.isContainable || false,
            isContained: engagement.isContained || false,
            orderTotal: typeof engagement.orderTotal === 'number' ? engagement.orderTotal : undefined,
            sessionSummaries: engagement.sessionSummaries
        };
    }

    return result;
};

// Transform engagements to transfer data records
// Optimized: Single pass filter+map, single moment() call per record
export const transformEngagementsToTransferRecords = (
    engagements: any[] | undefined,
    appNameMap: AppNameMap
): TransferDataRecord[] => {
    if (!engagements || engagements.length === 0) return [];

    // Use a manual loop with push for better performance than filter().map()
    // This avoids creating an intermediate filtered array
    const result: TransferDataRecord[] = [];

    for (let i = 0; i < engagements.length; i++) {
        const engagement = engagements[i];

        // Check if this engagement has a transfer reason
        // Only include engagements with an actual transfer reason to match chart data from API
        const hasPlannedReason = engagement.plannedTransferReason && engagement.plannedTransferReason.length > 0;
        const hasUnplannedReason = engagement.unplannedTransferReason && engagement.unplannedTransferReason.length > 0;
        const hasTransferReason = hasPlannedReason || hasUnplannedReason || (engagement.transferReason && engagement.transferReason.length > 0);

        // Skip engagements without an actual transfer reason
        if (!hasTransferReason) continue;

        const { date, time } = formatEventDateTime(engagement.eventDate);

        // Determine if planned based on available fields (handling empty strings)
        // Use explicit flags first, then fall back to reason-based mapping
        let isPlanned: boolean | null = null;
        if (engagement.isPlannedTransfer === true) {
            isPlanned = true;
        } else if (engagement.isUnplannedTransfer === true) {
            isPlanned = false;
        } else if (hasPlannedReason && !hasUnplannedReason) {
            isPlanned = true;
        } else if (hasUnplannedReason && !hasPlannedReason) {
            isPlanned = false;
        }

        // Get reason from appropriate field (use non-empty value)
        let reason: TransferReason = TransferReason.UNKNOWN;
        if (hasPlannedReason) {
            reason = engagement.plannedTransferReason as TransferReason;
        } else if (hasUnplannedReason) {
            reason = engagement.unplannedTransferReason as TransferReason;
        } else if (engagement.transferReason && engagement.transferReason.length > 0) {
            reason = engagement.transferReason as TransferReason;
        }

        // Note: Immediate transfers are now included in the data.
        // Filtering is handled at the UI level (modal pre-filters them out by default)

        // If isPlanned is still null and we have a reason, try to determine from the reason mapping
        if (isPlanned === null && reason !== TransferReason.UNKNOWN && TRANSFER_REASON_IS_PLANNED[reason] !== undefined) {
            isPlanned = TRANSFER_REASON_IS_PLANNED[reason];
        }

        result.push({
            id: engagement.id,
            date,
            time,
            phoneLastFour: engagement.nativeUserId?.slice(-4) || '****',
            transcriptLink: `/v/apps/${engagement.applicationId}/analytics?sessionId=${engagement.sessionId}`,
            transferType: isPlanned === true ? 'Planned' : isPlanned === false ? 'Unplanned' : 'Undetermined',
            transferReason: reason,
            location: appNameMap[engagement.applicationId] || engagement.applicationId,
            applicationId: engagement.applicationId,
            sessionId: engagement.sessionId,
            sessionSummaries: engagement.sessionSummaries
        });
    }

    return result;
};

// Helper to convert 12-hour time (h:mm A) to 24-hour sortable format
const convertTo24Hour = (time12: string): string => {
    const m = moment(time12, 'h:mm A');
    return m.isValid() ? m.format('HH:mm') : time12;
};

// Generic sort function for call and transfer data
// Default sort is by date+time descending (most recent first) when no sortConfig
export const sortDataRecords = <T extends CallDataRecord | TransferDataRecord>(
    data: T[],
    sortConfig: SortConfig | null,
    isTransfer: boolean = false
): T[] => {
    if (data.length === 0) return data;

    // Default to date descending (most recent first) when no sort config
    const effectiveConfig: SortConfig = sortConfig || { key: 'date', direction: 'desc' };

    // Only copy when actually sorting - this is the key optimization
    // Previously, [...data] was always called even when returning early
    const sorted = data.slice(); // slice() is slightly faster than spread for copying

    sorted.sort((a, b) => {
        let aVal: string | number = '';
        let bVal: string | number = '';

        switch (effectiveConfig.key) {
            case 'date':
                // Combine date and time for proper chronological sorting
                // Date is YYYY-MM-DD, time needs conversion from 12-hour to 24-hour
                aVal = `${a.date} ${convertTo24Hour(a.time)}`;
                bVal = `${b.date} ${convertTo24Hour(b.time)}`;
                break;
            case 'time':
                // Convert 12-hour to 24-hour for proper time sorting
                aVal = convertTo24Hour(a.time);
                bVal = convertTo24Hour(b.time);
                break;
            case 'phone':
                aVal = a.phoneLastFour;
                bVal = b.phoneLastFour;
                break;
            case 'location':
                aVal = a.location;
                bVal = b.location;
                break;
            case 'category':
                if ('category' in a && 'category' in b) {
                    aVal = a.category;
                    bVal = b.category;
                }
                break;
            case 'transferType':
                if ('transferType' in a && 'transferType' in b) {
                    aVal = a.transferType;
                    bVal = b.transferType;
                }
                break;
            case 'transferReason':
                if ('transferReason' in a && 'transferReason' in b) {
                    aVal = TRANSFER_REASON_LABELS[a.transferReason] || a.transferReason;
                    bVal = TRANSFER_REASON_LABELS[b.transferReason] || b.transferReason;
                }
                break;
        }

        if (aVal < bVal) return effectiveConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return effectiveConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return sorted;
};

// Helper to toggle sort
export const toggleSort = (
    key: string,
    currentConfig: SortConfig | null
): SortConfig | null => {
    if (currentConfig?.key === key) {
        return currentConfig.direction === 'asc'
            ? { key, direction: 'desc' }
            : null;
    }
    return { key, direction: 'asc' };
};

// Helper to convert CallDataRecords to TranscriptRecords for ChartDetailModal
export const callRecordsToTranscripts = (
    records: CallDataRecord[],
    categoryOverrideOrFn?: string | ((record: CallDataRecord) => string)
): TranscriptRecord[] => {
    return records.map(r => ({
        id: r.id,
        date: r.date,
        time: r.time,
        phoneLastFour: r.phoneLastFour,
        location: r.location,
        applicationId: r.applicationId,
        sessionId: r.sessionId,
        category: typeof categoryOverrideOrFn === 'function'
            ? categoryOverrideOrFn(r)
            : (categoryOverrideOrFn ?? r.category),
        tags: r.tags,
        sessionSummaries: r.sessionSummaries
    }));
};

// Helper to convert TransferDataRecords to TranscriptRecords for ChartDetailModal
export const transferRecordsToTranscripts = (
    records: TransferDataRecord[],
    categoryFormat?: (record: TransferDataRecord) => string
): TranscriptRecord[] => {
    return records.map(t => ({
        id: t.id,
        date: t.date,
        time: t.time,
        phoneLastFour: t.phoneLastFour,
        location: t.location,
        applicationId: t.applicationId,
        sessionId: t.sessionId,
        category: categoryFormat
            ? categoryFormat(t)
            : `${t.transferType} - ${TRANSFER_REASON_LABELS[t.transferReason] || t.transferReason}`,
        sessionSummaries: t.sessionSummaries
    }));
};

// Location summary interface for By Location tab
export interface LocationSummary {
    location: string;
    engagements: number;
    scheduled: number;
    rescheduled: number;
    cancelled: number;
    checkedNext: number;
    noInteraction: number;
    totalManagedCalls: number;
    supportedCallsPercent: number;
    totalManageableCalls: number;
    attempts: number;
    rawAttempts: number;
    nonContainable: number;
}

// Helper to count occurrences of a specific tag in the tags array
// Used to handle multipatient bookings where one call may have multiple occurrences of the same tag
const countTagOccurrences = (tags: string[], tag: string): number => {
    return tags.filter(t => t === tag).length;
};

// Helper function to aggregate call data by location
// Computes all metrics from the metricsDefinitions for the By Location tab
// NOTE: Counts tag occurrences (not just presence) to handle multipatient bookings
export const aggregateCallsByLocation = (callRecords: CallDataRecord[]): LocationSummary[] => {
    if (!callRecords || callRecords.length === 0) return [];

    // Group calls by location
    const locationMap = new Map<string, {
        engagements: number;
        scheduled: number;
        rescheduled: number;
        cancelled: number;
        checkedNext: number;
        noInteraction: number;
        rawAttempts: number;
        nonContainable: number;
    }>();

    for (const call of callRecords) {
        const loc = call.location || 'Unknown';

        if (!locationMap.has(loc)) {
            locationMap.set(loc, {
                engagements: 0,
                scheduled: 0,
                rescheduled: 0,
                cancelled: 0,
                checkedNext: 0,
                noInteraction: 0,
                rawAttempts: 0,
                nonContainable: 0
            });
        }

        const stats = locationMap.get(loc)!;
        stats.engagements++;

        // Check if non-containable (for % Supported Calls calculation only)
        const hasNonContainableTag = call.tags.includes('UNSUPPORTED_CUSTOMER_REQUEST') || call.tags.includes('UNSUPPORTED_APPOINTMENT_TYPE');

        // Count by tag occurrences - counts multiple appointments per call (multipatient bookings)
        // Count ALL occurrences to match Appointments Managed chart (source of truth)
        stats.scheduled += countTagOccurrences(call.tags, 'ACTION_COMPLETED');
        stats.rescheduled += countTagOccurrences(call.tags, 'APPOINTMENT_RESCHEDULED');
        stats.cancelled += countTagOccurrences(call.tags, 'APPOINTMENT_CANCELLED');
        stats.checkedNext += countTagOccurrences(call.tags, 'CHECK_NEXT_APPOINTMENT_COMPLETE');

        // Count by category
        if (call.category === 'NO_INTERACTION') stats.noInteraction++;

        // Raw Attempts = count of attempt tags within ACTION_ATTEMPTED category calls
        if (call.category === 'ACTION_ATTEMPTED') {
            stats.rawAttempts += countTagOccurrences(call.tags, 'SCHEDULE_ATTEMPTED');
            stats.rawAttempts += countTagOccurrences(call.tags, 'RESCHEDULE_ATTEMPTED');
            stats.rawAttempts += countTagOccurrences(call.tags, 'CANCELLATION_ATTEMPTED');
            stats.rawAttempts += countTagOccurrences(call.tags, 'CHECK_NEXT_APPOINTMENT');
        }

        // Non-containable = calls in ACTION_ATTEMPTED category that have UNSUPPORTED_CUSTOMER_REQUEST or UNSUPPORTED_APPOINTMENT_TYPE tags
        if (call.category === 'ACTION_ATTEMPTED' && hasNonContainableTag) {
            stats.nonContainable++;
        }
    }

    // Transform to LocationSummary array
    const summaries: LocationSummary[] = [];

    locationMap.forEach((stats, location) => {
        // Total Managed Calls = Scheduled + Rescheduled + Cancelled + Checked Next + No Interaction
        const totalManagedCalls = stats.scheduled + stats.rescheduled + stats.cancelled + stats.checkedNext + stats.noInteraction;

        // Attempts = Raw Attempts - Non-Containable
        const attempts = Math.max(0, stats.rawAttempts - stats.nonContainable);

        // % Supported Calls = (Scheduled + Rescheduled + Cancelled + Checked Next) / Attempts * 100
        const successfulActions = stats.scheduled + stats.rescheduled + stats.cancelled + stats.checkedNext;
        const supportedCallsPercent = attempts > 0 ? (successfulActions / attempts) * 100 : 0;

        // Total Manageable Calls = Managed Calls + Non-Containable
        const totalManageableCalls = totalManagedCalls + stats.nonContainable;

        summaries.push({
            location,
            engagements: stats.engagements,
            scheduled: stats.scheduled,
            rescheduled: stats.rescheduled,
            cancelled: stats.cancelled,
            checkedNext: stats.checkedNext,
            noInteraction: stats.noInteraction,
            totalManagedCalls,
            supportedCallsPercent,
            totalManageableCalls,
            attempts,
            rawAttempts: stats.rawAttempts,
            nonContainable: stats.nonContainable
        });
    });

    // Sort by location name alphabetically
    summaries.sort((a, b) => a.location.localeCompare(b.location));

    return summaries;
};

// Helper to check if an engagement is Spanish
// Checks isSpanish flag, language field, or 'SPANISH' tag in sessionTags array
export const isSpanishEngagement = (eng: any): boolean => {
    // Check boolean flag first
    if (eng.isSpanish === true) return true;

    // Normalize and check language field
    if (eng.language != null) {
        const normalizedLang = String(eng.language).trim().toLowerCase();
        if (normalizedLang === 'spanish' || normalizedLang === 'es') return true;
    }

    // Normalize and check sessionTags array
    if (eng.sessionTags && Array.isArray(eng.sessionTags)) {
        const hasSpanishTag = eng.sessionTags.some((tag: any) =>
            tag != null && String(tag).trim().toLowerCase() === 'spanish'
        );
        if (hasSpanishTag) return true;
    }

    return false;
};

// Sort function for location summaries
export const sortLocationSummaries = (
    data: LocationSummary[],
    sortConfig: SortConfig | null
): LocationSummary[] => {
    if (!sortConfig || data.length === 0) return data;

    const sorted = data.slice();

    sorted.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;

        switch (sortConfig.key) {
            case 'location':
                aVal = a.location.toLowerCase();
                bVal = b.location.toLowerCase();
                break;
            case 'engagements':
                aVal = a.engagements;
                bVal = b.engagements;
                break;
            case 'scheduled':
                aVal = a.scheduled;
                bVal = b.scheduled;
                break;
            case 'rescheduled':
                aVal = a.rescheduled;
                bVal = b.rescheduled;
                break;
            case 'cancelled':
                aVal = a.cancelled;
                bVal = b.cancelled;
                break;
            case 'checkedNext':
                aVal = a.checkedNext;
                bVal = b.checkedNext;
                break;
            case 'noInteraction':
                aVal = a.noInteraction;
                bVal = b.noInteraction;
                break;
            case 'totalManagedCalls':
                aVal = a.totalManagedCalls;
                bVal = b.totalManagedCalls;
                break;
            case 'supportedCallsPercent':
                aVal = a.supportedCallsPercent;
                bVal = b.supportedCallsPercent;
                break;
            case 'totalManageableCalls':
                aVal = a.totalManageableCalls;
                bVal = b.totalManageableCalls;
                break;
            case 'attempts':
                aVal = a.attempts;
                bVal = b.attempts;
                break;
            case 'rawAttempts':
                aVal = a.rawAttempts;
                bVal = b.rawAttempts;
                break;
            case 'nonContainable':
                aVal = a.nonContainable;
                bVal = b.nonContainable;
                break;
            default:
                return 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortConfig.direction === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === 'asc'
            ? (aVal as number) - (bVal as number)
            : (bVal as number) - (aVal as number);
    });

    return sorted;
};
